import { Component, signal, inject, computed } from '@angular/core';
import { CardComponent } from '../../shared/card/card.component';
import { SidebarService } from '../../services/sidebar.service';
import { DataService } from '../../services/data.service';
import { FeatureCompact } from '../../models/geo-json-feature';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { APP_PATHS } from '../../constants/routes.constants';
import { TranslateModule } from '@ngx-translate/core';

interface Category {
  id: string;
  label: string;
  icon?: string;
}

@Component({
  selector: 'app-categories',
  imports: [CardComponent, RouterLink, TranslateModule],
  templateUrl: './categories.component.html',
  styleUrl: './categories.component.css'
})
export class CategoriesComponent {
  public sidebarService = inject(SidebarService);
  private dataService = inject(DataService);
  // private filterService = inject(FilterService); // Removed
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  paths = APP_PATHS;

  searchQuery = signal('');
  selectedCategories = signal<Set<string>>(new Set(['todos']));
  features = signal<FeatureCompact[]>([]);

  isMobileFiltersOpen = this.sidebarService.isFilterMenuOpen;

  constructor() {
    // Listen to Query Params
    this.route.queryParams.subscribe(params => {
      const cats = params['category'];
      const newSet = new Set<string>();

      if (cats) {
        if (Array.isArray(cats)) {
          cats.forEach((c: string) => newSet.add(c.toLowerCase()));
        } else {
          newSet.add(cats.toLowerCase());
        }
      } else {
        newSet.add('todos');
      }
      this.selectedCategories.set(newSet);
    });

    console.log('CategoriesComponent Init - Reading URL Params');

    this.dataService.getCompactFeatures().subscribe(data => {
      this.features.set(data);
    });
  }

  categories: Category[] = [
    { id: 'todos', label: 'CATEGORIES.ALL' },
    {
      id: 'arq',
      label: 'CATEGORIES.ARQ',
      icon: '/assets/icons/arquitectura.png'
    },
    {
      id: 'natural',
      label: 'CATEGORIES.NATURAL',
      icon: '/assets/icons/naturaleza.png'
    },
    {
      id: 'evento',
      label: 'CATEGORIES.EVENTO',
      icon: '/assets/icons/eventos.png'
    },
    {
      id: 'sendero',
      label: 'CATEGORIES.SENDERO',
      icon: '/assets/icons/senderos.png'
    },
    {
      id: 'pueblo',
      label: 'CATEGORIES.PUEBLO',
      icon: '/assets/icons/pueblos.png'
    },
    {
      id: 'playa',
      label: 'CATEGORIES.PLAYA',
      icon: '/assets/icons/playas.png'
    }
  ];

  filteredCards = computed(() => {
    // Excluir alojamientos de la página de maravillas
    let filtered = this.features().filter(f => f.categoria?.toLowerCase() !== 'alo');

    // Filter by category (multiple selection)
    const selected = this.selectedCategories();
    if (!selected.has('todos')) {
      filtered = filtered.filter(feature => {
        // Normalize category to match filter IDs (lowercase)
        const catId = feature.categoria?.toLowerCase();
        return selected.has(catId);
      });
    }

    // Filter by search query
    const query = this.searchQuery().trim();
    if (query) {
      const lowerQuery = query.toLowerCase();
      filtered = filtered.filter(feature =>
        feature.denominacion.toLowerCase().includes(lowerQuery) ||
        feature.categoria.toLowerCase().includes(lowerQuery)
      );
    }

    // Ordenar alfabéticamente
    filtered = [...filtered].sort((a, b) => a.denominacion.localeCompare(b.denominacion));

    return filtered;
  });

  selectCategory(categoryId: string): void {
    const current = new Set(this.selectedCategories());

    if (categoryId === 'todos') {
      current.clear();
      current.add('todos');
    } else {
      current.delete('todos');
      if (current.has(categoryId)) {
        current.delete(categoryId);
        if (current.size === 0) current.add('todos');
      } else {
        current.add(categoryId);
      }
    }


    // Update URL
    const catsArray = Array.from(current);
    // If 'todos' is the only selected, remove param
    const queryParams: any = {};

    if (catsArray.length === 1 && catsArray[0] === 'todos') {
      queryParams['category'] = null; // Remove param
    } else {
      queryParams['category'] = catsArray;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge'
    });

    // this.filterService.setWondersCategories(current); // Removed
    // this.selectedCategories is updated via subscription
  }

  isCategorySelected(categoryId: string): boolean {
    return this.selectedCategories().has(categoryId);
  }

  updateSearchQuery(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery.set(input.value);
  }

  toggleMobileFilters(): void {
    this.sidebarService.toggleFilterMenu();
  }

  closeMobileFilters(): void {
    this.sidebarService.closeFilterMenu();
  }

  isAccommodation(category: string): boolean {
    return category?.toLowerCase().includes('alojamiento');
  }
}
